import { useState, useEffect, useCallback } from "react";
import { useAuth, apiClient, useTheme } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  GraduationCap,
  Calendar,
  Sun,
  Moon,
  Settings,
  LogOut,
  CreditCard,
  ChevronRight,
  Target,
  TrendingUp,
  Shield,
  CheckCircle2,
  PlayCircle,
  Circle,
} from "lucide-react";
import { motion } from "framer-motion";

const CollegesByCategory = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const response = await apiClient.get("/colleges/by-category");
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "submitted":
        return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case "in_progress":
        return <PlayCircle className="w-4 h-4 text-violet-500" />;
      default:
        return <Circle className="w-4 h-4 text-zinc-400" />;
    }
  };

  const categoryConfig = {
    reach: {
      label: "Reach Schools",
      description: "Dream schools with lower acceptance chances",
      icon: TrendingUp,
      color: "rose",
      bgClass: "bg-rose-500",
      bgLightClass: "bg-rose-100 dark:bg-rose-900/30",
      textClass: "text-rose-600 dark:text-rose-400",
    },
    target: {
      label: "Target Schools",
      description: "Schools matching your profile",
      icon: Target,
      color: "amber",
      bgClass: "bg-amber-500",
      bgLightClass: "bg-amber-100 dark:bg-amber-900/30",
      textClass: "text-amber-600 dark:text-amber-400",
    },
    safety: {
      label: "Safety Schools",
      description: "High-confidence acceptance schools",
      icon: Shield,
      color: "emerald",
      bgClass: "bg-emerald-500",
      bgLightClass: "bg-emerald-100 dark:bg-emerald-900/30",
      textClass: "text-emerald-600 dark:text-emerald-400",
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalColleges = data ? 
    data.colleges.reach.length + data.colleges.target.length + data.colleges.safety.length : 0;

  return (
    <div className="min-h-screen bg-background noise-bg pb-20 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-8 h-8 text-primary" />
              <span className="font-bold text-xl font-outfit">Admit List</span>
            </div>

            <nav className="hidden md:flex items-center gap-6">
              <Button
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => navigate("/dashboard")}
                data-testid="nav-dashboard-btn"
              >
                Dashboard
              </Button>
              <Button
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => navigate("/calendar")}
                data-testid="nav-calendar-btn"
              >
                Calendar
              </Button>
              <Button
                variant="ghost"
                className="text-foreground"
                onClick={() => navigate("/categories")}
                data-testid="nav-categories-btn"
              >
                Categories
              </Button>
            </nav>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle-btn"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-btn">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback>{user?.name?.[0] || "U"}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/subscription")} data-testid="subscription-menu-item">
                    <CreditCard className="mr-2 h-4 w-4" />
                    Subscription
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-menu-item">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-8 py-8">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight font-outfit mb-2">
            Schools by Category
          </h1>
          <p className="text-muted-foreground">
            View your college list organized by reach, target, and safety schools.
          </p>
        </motion.div>

        {/* Overall Stats */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-1 w-full">
                  <h3 className="text-lg font-semibold font-outfit mb-4">Application Balance</h3>
                  <div className="flex items-center gap-2 mb-4">
                    {totalColleges > 0 ? (
                      <>
                        <div 
                          className="h-3 rounded-full bg-rose-500 transition-all"
                          style={{ width: `${(data.colleges.reach.length / totalColleges) * 100}%` }}
                        />
                        <div 
                          className="h-3 rounded-full bg-amber-500 transition-all"
                          style={{ width: `${(data.colleges.target.length / totalColleges) * 100}%` }}
                        />
                        <div 
                          className="h-3 rounded-full bg-emerald-500 transition-all"
                          style={{ width: `${(data.colleges.safety.length / totalColleges) * 100}%` }}
                        />
                      </>
                    ) : (
                      <div className="h-3 w-full rounded-full bg-secondary" />
                    )}
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-rose-500" />
                      <span>Reach ({data?.colleges.reach.length || 0})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-amber-500" />
                      <span>Target ({data?.colleges.target.length || 0})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-emerald-500" />
                      <span>Safety ({data?.colleges.safety.length || 0})</span>
                    </div>
                  </div>
                </div>
                <div className="text-center md:text-right">
                  <p className="text-4xl font-bold font-mono">{totalColleges}</p>
                  <p className="text-sm text-muted-foreground">Total Schools</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Category Sections */}
        <div className="space-y-8">
          {["reach", "target", "safety"].map((category, catIndex) => {
            const config = categoryConfig[category];
            const colleges = data?.colleges[category] || [];
            const stats = data?.stats[category] || { total: 0, submitted: 0, in_progress: 0, not_started: 0 };
            const Icon = config.icon;
            const submittedPercent = stats.total > 0 ? Math.round((stats.submitted / stats.total) * 100) : 0;

            return (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 + catIndex * 0.1 }}
              >
                <Card>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl ${config.bgLightClass} flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${config.textClass}`} />
                        </div>
                        <div>
                          <CardTitle className="font-outfit">{config.label}</CardTitle>
                          <p className="text-sm text-muted-foreground">{config.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold font-mono">{stats.total}</p>
                        <p className="text-xs text-muted-foreground">
                          {stats.submitted} submitted
                        </p>
                      </div>
                    </div>
                    {stats.total > 0 && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-mono">{submittedPercent}% submitted</span>
                        </div>
                        <Progress value={submittedPercent} className="h-2" />
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    {colleges.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>No {config.label.toLowerCase()} added yet</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4 rounded-full"
                          onClick={() => navigate("/dashboard")}
                        >
                          Add Schools
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {colleges.map((college) => (
                          <div
                            key={college.college_id}
                            className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 hover:bg-secondary cursor-pointer transition-colors"
                            onClick={() => navigate(`/college/${college.college_id}`)}
                            data-testid={`category-college-${college.college_id}`}
                          >
                            <div className="flex items-center gap-3">
                              {getStatusIcon(college.status)}
                              <div>
                                <p className="font-medium">{college.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {college.application_type}
                                  {college.deadline && college.deadline !== "Rolling" && (
                                    <> • Due: {college.deadline}</>
                                  )}
                                </p>
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Recommendation Card */}
        {totalColleges > 0 && (
          <motion.div
            className="mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            <Card className="bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-violet-200 dark:border-violet-900/50">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold font-outfit mb-2">Application Strategy Tip</h3>
                <p className="text-muted-foreground text-sm">
                  {data?.colleges.reach.length === 0 && "Consider adding some reach schools to aim high!"}
                  {data?.colleges.safety.length === 0 && data?.colleges.reach.length > 0 && "Don't forget to add safety schools for peace of mind."}
                  {data?.colleges.target.length === 0 && data?.colleges.reach.length > 0 && data?.colleges.safety.length > 0 && "Add some target schools that match your profile."}
                  {data?.colleges.reach.length > 0 && data?.colleges.target.length > 0 && data?.colleges.safety.length > 0 && (
                    <>
                      Great balance! A typical strategy is 2-3 reach, 3-4 target, and 2-3 safety schools.
                      You have {data.colleges.reach.length} reach, {data.colleges.target.length} target, and {data.colleges.safety.length} safety schools.
                    </>
                  )}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="mobile-nav p-2">
        <div className="flex items-center justify-around">
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/dashboard")}
            data-testid="mobile-nav-dashboard"
          >
            <GraduationCap className="w-5 h-5" />
            <span className="text-xs">Dashboard</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/calendar")}
            data-testid="mobile-nav-calendar"
          >
            <Calendar className="w-5 h-5" />
            <span className="text-xs">Calendar</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2 text-primary"
            onClick={() => navigate("/categories")}
            data-testid="mobile-nav-categories"
          >
            <Target className="w-5 h-5" />
            <span className="text-xs">Categories</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/settings")}
            data-testid="mobile-nav-settings"
          >
            <Settings className="w-5 h-5" />
            <span className="text-xs">Settings</span>
          </Button>
        </div>
      </nav>
    </div>
  );
};

export default CollegesByCategory;
